/*
 * Strategic Metrics Component
 * Displays key strategic tourism metrics with trends and comparisons
 * Follows Communication Dashboard design system patterns
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  Clock,
  Star,
  Target,
  Activity,
  BarChart3
} from 'lucide-react';
import { motion } from 'framer-motion';

interface MetricData {
  label: string;
  value: number;
  formattedValue: string;
  previousValue: number;
  change: number;
  changePercentage: number;
  trend: 'up' | 'down' | 'stable';
  target?: number;
  targetPercentage?: number;
  icon: React.ReactNode;
  color: 'emerald' | 'blue' | 'purple' | 'amber' | 'red';
}

interface StrategicMetricsProps {
  className?: string;
}

export function StrategicMetrics({ className = "" }: StrategicMetricsProps) {
  const [metrics] = useState<MetricData[]>([
    {
      label: 'Monthly Revenue',
      value: 45720,
      formattedValue: '$45,720',
      previousValue: 38240,
      change: 7480,
      changePercentage: 19.6,
      trend: 'up',
      target: 50000,
      targetPercentage: 91.4,
      icon: <DollarSign className="w-5 h-5" />,
      color: 'emerald'
    },
    {
      label: 'Conversion Rate',
      value: 23.4,
      formattedValue: '23.4%',
      previousValue: 18.7,
      change: 4.7,
      changePercentage: 25.1,
      trend: 'up',
      target: 30,
      targetPercentage: 78,
      icon: <Target className="w-5 h-5" />,
      color: 'blue'
    },
    {
      label: 'Avg Booking Value',
      value: 285,
      formattedValue: '$285',
      previousValue: 242,
      change: 43,
      changePercentage: 17.8,
      trend: 'up',
      icon: <BarChart3 className="w-5 h-5" />,
      color: 'purple'
    },
    {
      label: 'Customer Satisfaction',
      value: 4.7,
      formattedValue: '4.7/5.0',
      previousValue: 4.5,
      change: 0.2,
      changePercentage: 4.4,
      trend: 'up',
      target: 5.0,
      targetPercentage: 94,
      icon: <Star className="w-5 h-5" />,
      color: 'amber'
    }
  ]);

  const getColorClasses = (color: MetricData['color']) => {
    switch (color) {
      case 'emerald':
        return {
          bg: 'bg-gradient-to-br from-emerald-100 to-emerald-200',
          border: 'border-emerald-200/50',
          text: 'text-emerald-700',
          badge: 'bg-emerald-100 text-emerald-800 border border-emerald-200/50'
        };
      case 'blue':
        return {
          bg: 'bg-gradient-to-br from-blue-100 to-blue-200',
          border: 'border-blue-200/50',
          text: 'text-blue-700',
          badge: 'bg-blue-100 text-blue-800 border border-blue-200/50'
        };
      case 'purple':
        return {
          bg: 'bg-gradient-to-br from-purple-100 to-purple-200',
          border: 'border-purple-200/50',
          text: 'text-purple-700',
          badge: 'bg-purple-100 text-purple-800 border border-purple-200/50'
        };
      case 'amber':
        return {
          bg: 'bg-gradient-to-br from-amber-100 to-amber-200',
          border: 'border-amber-200/50',
          text: 'text-amber-700',
          badge: 'bg-amber-100 text-amber-800 border border-amber-200/50'
        };
      case 'red':
        return {
          bg: 'bg-gradient-to-br from-red-100 to-red-200',
          border: 'border-red-200/50',
          text: 'text-red-700',
          badge: 'bg-red-100 text-red-800 border border-red-200/50'
        };
    }
  };

  const getTrendIcon = (trend: MetricData['trend']) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-emerald-600" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      case 'stable':
        return <div className="w-4 h-4 flex items-center justify-center">
          <div className="w-3 h-0.5 bg-slate-600 rounded" />
        </div>;
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Strategic Metrics</h2>
            <p className="text-sm text-slate-600">Key performance indicators and trends</p>
          </div>
        </div>
        <Badge variant="secondary" className="bg-purple-100 text-purple-800 border border-purple-200/50">
          Strategic Intelligence
        </Badge>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => {
          const colorClasses = getColorClasses(metric.color);
          return (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300 h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className={`w-10 h-10 rounded-lg ${colorClasses.bg} ${colorClasses.border} border flex items-center justify-center shadow-sm`}>
                      <div className={colorClasses.text}>
                        {metric.icon}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {getTrendIcon(metric.trend)}
                      <Badge variant="secondary" className={colorClasses.badge}>
                        {metric.changePercentage > 0 ? '+' : ''}{metric.changePercentage.toFixed(1)}%
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  
                  {/* Main Value */}
                  <div>
                    <div className="text-2xl font-bold text-slate-900">
                      {metric.formattedValue}
                    </div>
                    <CardTitle className="text-sm font-medium text-slate-600 mt-1">
                      {metric.label}
                    </CardTitle>
                  </div>

                  {/* Target Progress (if applicable) */}
                  {metric.target && metric.targetPercentage && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-600">Target Progress</span>
                        <span className="font-medium text-slate-900">{metric.targetPercentage.toFixed(1)}%</span>
                      </div>
                      <Progress value={metric.targetPercentage} className="h-2" />
                    </div>
                  )}

                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

    </div>
  );
} 